// src/pages/Employee/CreateEmployee/steps/Step4JobSalaryBank.tsx

import React, { useState } from 'react';
import { Row, Col, FormGroup, Label, Input, FormFeedback, Badge, Button } from 'reactstrap';
import { JobSalaryBankData, StepProps } from '../../types/employee.types';

export interface LookupOption {
  id: number;
  label: string;
}

interface Step4Props extends StepProps<JobSalaryBankData> {
  designations: LookupOption[];
  units: LookupOption[];
  employeeLocations: LookupOption[];
  banks: LookupOption[];
  loadingLookups?: boolean;
}

const EMPLOYEE_TYPES = [
  { value: '1', label: 'Office' },
  { value: '2', label: 'Site Engineer' },
  { value: '3', label: 'Executive' },
];

const NATURE_OF_JOB = [
  { value: '1', label: 'Probation Period' },
  { value: '2', label: 'Contract' },
  { value: '3', label: 'Regular' },
  { value: '4', label: 'Internship Paid' },
  { value: '5', label: 'Internship Unpaid' },
];

const Step4JobSalaryBank: React.FC<Step4Props> = ({
  data,
  onChange,
  errors,
  designations,
  units,
  employeeLocations,
  banks,
  loadingLookups,
}) => {
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  const set = (field: keyof JobSalaryBankData, value: any) => {
    onChange({ ...data, [field]: value });
  };

  // Check if nature of job is Probation (1) or Contract (2)
  const showProbationMonths = data.nature_of_job === '1';
  const showContractEndDate = data.nature_of_job === '2';

  // Handle multi-select for employee locations
  const toggleLocation = (locationId: number) => {
    const currentLocations = data.employee_location_ids || [];
    if (currentLocations.includes(locationId)) {
      // Remove location
      set('employee_location_ids', currentLocations.filter(id => id !== locationId));
    } else {
      // Add location
      set('employee_location_ids', [...currentLocations, locationId]);
    }
  };

  const removeLocation = (locationId: number) => {
    const currentLocations = data.employee_location_ids || [];
    set('employee_location_ids', currentLocations.filter(id => id !== locationId));
  };

  const getLocationLabel = (id: number) => {
    const location = employeeLocations.find(l => l.id === id);
    return location ? location.label : '';
  };

  // Render selected locations as badges
  const renderSelectedLocations = () => {
    const selectedIds = data.employee_location_ids || [];
    if (selectedIds.length === 0) {
      return <span className="text-muted small">No locations selected</span>;
    }
    return (
      <div className="d-flex flex-wrap gap-1 mt-1">
        {selectedIds.map((id) => (
          <Badge key={id} color="primary" className="d-flex align-items-center gap-1 p-2">
            {getLocationLabel(id)}
            <Button
              color="link"
              size="sm"
              className="p-0 text-white"
              onClick={() => removeLocation(id)}
              style={{ fontSize: '12px', lineHeight: '1' }}
            >
              ×
            </Button>
          </Badge>
        ))}
      </div>
    );
  };

  return (
    <div>

      {/* ===================== GEO-FENCE & FACE DETECTION ===================== */}
      <div className="border-top pt-3 mt-3">
        <h6 className="mb-3">Geo-Fence &amp; Face Detection Settings</h6>

        <Row>
          <Col md={12}>
            <FormGroup>
              <div className="d-flex align-items-center flex-wrap" style={{ gap: '24px' }}>
                {/* Geo-Fence */}
                <div
                  className="d-flex align-items-center"
                  style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    backgroundColor: '#f8f9fa',
                    minWidth: '260px',
                  }}
                >
                  <Label className="fw-bold text-muted small text-uppercase mb-0 me-3">
                    Geo-Fence:
                  </Label>
                  <div className="d-flex gap-3">
                    <FormGroup check className="mb-0">
                      <Label check className="fw-normal">
                        <Input
                          type="radio"
                          name="geofence"
                          value="on"
                          checked={data.geofence === true}
                          onChange={() => set('geofence', true)}
                        />
                        <span className="ms-1 text-success">On</span>
                      </Label>
                    </FormGroup>
                    <FormGroup check className="mb-0">
                      <Label check className="fw-normal">
                        <Input
                          type="radio"
                          name="geofence"
                          value="off"
                          checked={data.geofence === false}
                          onChange={() => set('geofence', false)}
                        />
                        <span className="ms-1 text-danger">Off</span>
                      </Label>
                    </FormGroup>
                  </div>
                </div>

                {/* Face Detection */}
                <div
                  className="d-flex align-items-center"
                  style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    backgroundColor: '#f8f9fa',
                    minWidth: '260px',
                  }}
                >
                  <Label className="fw-bold text-muted small text-uppercase mb-0 me-3">
                    Face Detection:
                  </Label>
                  <div className="d-flex gap-3">
                    <FormGroup check className="mb-0">
                      <Label check className="fw-normal">
                        <Input
                          type="radio"
                          name="facedetection"
                          value="yes"
                          checked={data.facedetection === true}
                          onChange={() => set('facedetection', true)}
                        />
                        <span className="ms-1 text-success">Yes</span>
                      </Label>
                    </FormGroup>
                    <FormGroup check className="mb-0">
                      <Label check className="fw-normal">
                        <Input
                          type="radio"
                          name="facedetection"
                          value="no"
                          checked={data.facedetection === false}
                          onChange={() => set('facedetection', false)}
                        />
                        <span className="ms-1 text-danger">No</span>
                      </Label>
                    </FormGroup>
                  </div>
                </div>

                {/* ✅ Web App (is_pwa) — same visual pattern as the two above */}
                <div
                  className="d-flex align-items-center"
                  style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    backgroundColor: '#f8f9fa',
                    minWidth: '260px',
                  }}
                >
                  <Label className="fw-bold text-muted small text-uppercase mb-0 me-3">
                    Web App:
                  </Label>
                  <div className="d-flex gap-3">
                    <FormGroup check className="mb-0">
                      <Label check className="fw-normal">
                        <Input
                          type="radio"
                          name="is_pwa"
                          value="yes"
                          checked={data.is_pwa === true}
                          onChange={() => set('is_pwa', true)}
                        />
                        <span className="ms-1 text-success">Yes</span>
                      </Label>
                    </FormGroup>
                    <FormGroup check className="mb-0">
                      <Label check className="fw-normal">
                        <Input
                          type="radio"
                          name="is_pwa"
                          value="no"
                          checked={data.is_pwa === false}
                          onChange={() => set('is_pwa', false)}
                        />
                        <span className="ms-1 text-danger">No</span>
                      </Label>
                    </FormGroup>
                  </div>
                </div>
              </div>

              <div className="text-muted small mt-2">
                <i className="ri-information-line me-1"></i>
                Geo-Fence requires employee to be within assigned office radius. Face Detection requires a selfie to mark attendance. Web App grants access to the PWA.
              </div>
            </FormGroup>
          </Col>
        </Row>
      </div>

      {/* ===================== JOB DETAILS ===================== */}
      <h6 className="mb-3">Job Details</h6>

      <Row>
        <Col md={4}>
          <FormGroup>
            <Label className="fw-bold text-muted small text-uppercase">
              Date of Joining <span className="text-danger">*</span>
            </Label>
            <Input
              type="date"
              value={data.date_of_joining}
              invalid={!!errors.date_of_joining}
              onChange={(e) => set('date_of_joining', e.target.value)}
            />
            <FormFeedback>{errors.date_of_joining}</FormFeedback>
          </FormGroup>
        </Col>

        <Col md={4}>
          <FormGroup>
            <Label className="fw-bold text-muted small text-uppercase">Designation</Label>
            <Input
              type="select"
              value={data.designation_id}
              onChange={(e) => set('designation_id', e.target.value)}
              disabled={loadingLookups}
            >
              <option value="">Select...</option>
              {designations.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>

        <Col md={4}>
          <FormGroup>
            <Label className="fw-bold text-muted small text-uppercase">company</Label>
            <Input
              type="select"
              value={data.unit_id}
              onChange={(e) => set('unit_id', e.target.value)}
              disabled={loadingLookups}
            >
              <option value="">Select...</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <FormGroup>
            <Label className="fw-bold text-muted small text-uppercase">
              Employee Type <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              value={data.employee_type}
              invalid={!!errors.employee_type}
              onChange={(e) => set('employee_type', e.target.value)}
            >
              <option value="">Select...</option>
              {EMPLOYEE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Input>
            <FormFeedback>{errors.employee_type}</FormFeedback>
          </FormGroup>
        </Col>

        <Col md={4}>
          <FormGroup>
            <Label className="fw-bold text-muted small text-uppercase">
              Employee Location
              {/* Show required asterisk only when geofence is enabled */}
              {data.geofence === true && <span className="text-danger">*</span>}
            </Label>
            <div
              className={`position-relative form-control ${errors.employee_location_ids ? 'is-invalid' : ''
                }`}
              style={{
                cursor: 'pointer',
                backgroundColor: '#fff',
                minHeight: '38px',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                padding: '4px 8px',
                borderColor: errors.employee_location_ids ? '#dc3545' : '#ced4da',
              }}
              onClick={() => setShowLocationDropdown(!showLocationDropdown)}
            >
              {renderSelectedLocations()}
              <span className="text-muted small" style={{ marginLeft: 'auto' }}>
                {showLocationDropdown ? '▲' : '▼'}
              </span>
            </div>
            {errors.employee_location_ids && (
              <div className="invalid-feedback" style={{ display: 'block' }}>
                {errors.employee_location_ids}
              </div>
            )}
            {showLocationDropdown && (
              <div
                className="dropdown-menu show"
                style={{
                  width: '100%',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  zIndex: 1000,
                }}
              >
                {employeeLocations.length === 0 ? (
                  <div className="dropdown-item text-muted">No locations available</div>
                ) : (
                  employeeLocations.map((location) => {
                    const isSelected = (data.employee_location_ids || []).includes(location.id);
                    return (
                      <div
                        key={location.id}
                        className={`dropdown-item ${isSelected ? 'active' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleLocation(location.id)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => { }}
                          className="me-2"
                        />
                        {location.label}
                      </div>
                    );
                  })
                )}
              </div>
            )}
            <div className="text-muted small mt-1">
              {data.geofence === true ? (
                <span className="text-danger">
                  <i className="ri-information-line me-1"></i>
                  Required: Select at least one location when Geo-Fence is enabled
                </span>
              ) : (
                <span>
                  <i className="ri-information-line me-1"></i>
                  Select one or more office locations (optional)
                </span>
              )}
            </div>
          </FormGroup>
        </Col>

        <Col md={4}>
          <FormGroup>
            <Label className="fw-bold text-muted small text-uppercase">
              Nature of Job <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              value={data.nature_of_job}
              invalid={!!errors.nature_of_job}
              onChange={(e) => set('nature_of_job', e.target.value)}
            >
              <option value="">Select...</option>
              {NATURE_OF_JOB.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Input>
            <FormFeedback>{errors.nature_of_job}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>

      {/* Conditional fields based on Nature of Job */}
      {(showProbationMonths || showContractEndDate) && (
        <Row>
          {showProbationMonths && (
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold text-muted small text-uppercase">
                  Probation Period (months)
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={data.probation_period_months}
                  onChange={(e) => set('probation_period_months', e.target.value)}
                  placeholder="e.g. 6"
                />
                <div className="text-muted small mt-1">
                  Enter probation duration in months
                </div>
              </FormGroup>
            </Col>
          )}

          {showContractEndDate && (
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold text-muted small text-uppercase">
                  Contract End Date
                </Label>
                <Input
                  type="date"
                  value={data.contract_end_date}
                  onChange={(e) => set('contract_end_date', e.target.value)}
                />
                <div className="text-muted small mt-1">
                  Date when the contract ends
                </div>
              </FormGroup>
            </Col>
          )}
        </Row>
      )}

      {/* ===================== SALARY & STATUTORY ===================== */}
      <div className="border-top pt-3 mt-3">
        <h6 className="mb-3">Salary &amp; Statutory</h6>

        <Row>
          <Col md={4}>
            <FormGroup>
              <Label className="fw-bold text-muted small text-uppercase">Basic Salary</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={data.basic_salary}
                invalid={!!errors.basic_salary}
                onChange={(e) => set('basic_salary', e.target.value)}
                placeholder="0.00"
              />
              <FormFeedback>{errors.basic_salary}</FormFeedback>
            </FormGroup>
          </Col>

          <Col md={4}>
            <FormGroup>
              <Label className="fw-bold text-muted small text-uppercase">Take Home Salary</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={data.take_home_salary}
                invalid={!!errors.take_home_salary}
                onChange={(e) => set('take_home_salary', e.target.value)}
                placeholder="0.00"
              />
              <FormFeedback>{errors.take_home_salary}</FormFeedback>
            </FormGroup>
          </Col>

          <Col md={4}>
            <FormGroup>
              <Label className="fw-bold text-muted small text-uppercase">Site Allowance</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={data.site_allowance}
                invalid={!!errors.site_allowance}
                onChange={(e) => set('site_allowance', e.target.value)}
                placeholder="0.00"
              />
              <FormFeedback>{errors.site_allowance}</FormFeedback>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <FormGroup>
              <Label className="fw-bold text-muted small text-uppercase">Payment Mode</Label>
              <Input
                type="select"
                value={data.payment_mode}
                onChange={(e) => set('payment_mode', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="1">Bank Transfer</option>
                <option value="2">Cash</option>
                <option value="3">Cheque</option>
              </Input>
            </FormGroup>
          </Col>

          <Col md={4}>
            <FormGroup>
              <Label className="fw-bold text-muted small text-uppercase">Salary Calculation</Label>
              <Input
                type="select"
                value={data.salary_calculation}
                onChange={(e) => set('salary_calculation', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="1"> Biometric </option>
                <option value="2">Manual</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={3}>
            <FormGroup className="mt-2">
              <Label className="fw-bold text-muted small text-uppercase">Paid Leaves</Label>
              <Input
                type="number"
                min={0}
                value={data.paid_leaves}
                onChange={(e) => set('paid_leaves', e.target.value)}
                placeholder="0"
              />
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup check className="mt-4">
              <Input
                type="checkbox"
                id="pfEsiApplicable"
                checked={data.pf_esi_applicable}
                onChange={(e) => set('pf_esi_applicable', e.target.checked)}
              />
              <Label check htmlFor="pfEsiApplicable" className="fw-bold">
                PF / ESI Applicable
              </Label>
            </FormGroup>
          </Col>

          <Col md={3}>
            <FormGroup check className="mt-4">
              <Input
                type="checkbox"
                id="overtime"
                checked={data.overtime}
                onChange={(e) => set('overtime', e.target.checked)}
              />
              <Label check htmlFor="overtime" className="fw-bold">
                Eligible for Overtime
              </Label>
            </FormGroup>
          </Col>
        </Row>

        {/* PF/ESI Details - Conditional */}
        {data.pf_esi_applicable && (
          <Row className="mt-2">
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold text-muted small text-uppercase">
                  PF/ESI Gross Salary
                </Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={data.pf_esi_gross_salary}
                  onChange={(e) => set('pf_esi_gross_salary', e.target.value)}
                  placeholder="0.00"
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold text-muted small text-uppercase">PF Number</Label>
                <Input
                  value={data.pf_number}
                  onChange={(e) => set('pf_number', e.target.value.toUpperCase())}
                  placeholder="Enter PF number"
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold text-muted small text-uppercase">ESI Number</Label>
                <Input
                  value={data.esi_number}
                  onChange={(e) => set('esi_number', e.target.value)}
                  placeholder="Enter ESI number"
                />
              </FormGroup>
            </Col>
          </Row>
        )}
      </div>

      {/* ===================== BANK DETAILS ===================== */}
      <div className="border-top pt-3 mt-3">
        <h6 className="mb-3">Bank Details</h6>

        <Row>
          <Col md={4}>
            <FormGroup>
              <Label className="fw-bold text-muted small text-uppercase">Bank</Label>
              <Input
                type="select"
                value={data.bank_id}
                onChange={(e) => set('bank_id', e.target.value)}
                disabled={loadingLookups}
              >
                <option value="">Select...</option>
                {banks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>

          <Col md={4}>
            <FormGroup>
              <Label className="fw-bold text-muted small text-uppercase">Account Number</Label>
              <Input
                value={data.bank_account_no}
                invalid={!!errors.bank_account_no}
                onChange={(e) => set('bank_account_no', e.target.value.replace(/\D/g, ''))}
                placeholder="Enter account number"
              />
              <FormFeedback>{errors.bank_account_no}</FormFeedback>
            </FormGroup>
          </Col>

          <Col md={4}>
            <FormGroup>
              <Label className="fw-bold text-muted small text-uppercase">IFSC Code</Label>
              <Input
                value={data.ifsc_code}
                invalid={!!errors.ifsc_code}
                style={{ textTransform: 'uppercase' }}
                onChange={(e) => set('ifsc_code', e.target.value.toUpperCase())}
                placeholder="e.g. SBIN0001234"
              />
              <FormFeedback>{errors.ifsc_code}</FormFeedback>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <FormGroup>
              <Label className="fw-bold text-muted small text-uppercase">Branch</Label>
              <Input
                value={data.bank_branch}
                onChange={(e) => set('bank_branch', e.target.value)}
                placeholder="Branch name"
              />
            </FormGroup>
          </Col>
        </Row>
      </div>

    </div>
  );
};

export default Step4JobSalaryBank;